import Link from "next/link";
import { CALENDLY_URL } from "@/common/site.config";
import {
  AudienceSplit,
  CtaBand,
  FaqList,
  ProcessRail,
  SectionHeader,
} from "@/components/main/landing/landing-ui";

export function ServiceSection({ children, className = "", muted = false }) {
  return (
    <section
      className={`py-14 md:py-20 ${
        muted ? "border-y border-line bg-muted/50" : ""
      } ${className}`}
    >
      <div className="container max-w-7xl">{children}</div>
    </section>
  );
}

export function ServiceSectionHeader({ title, lead, align = "left" }) {
  return (
    <SectionHeader
      title={title}
      lead={lead}
      align={align}
    />
  );
}

export function FeatureGrid({ items }) {
  return (
    <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <div
          key={item.title}
          className="rounded-2xl border border-line bg-surface p-6 shadow-[0_12px_32px_-22px_rgba(15,28,26,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#5aada4]/55"
        >
          {item.number ? (
            <p className="mb-2 text-sm font-semibold text-ink-soft">
              {item.number}
            </p>
          ) : null}
          <h3 className="text-lg font-semibold text-ink">{item.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink-muted">
            {item.description}
          </p>
        </div>
      ))}
    </div>
  );
}

export function SplitContent({ title, lead, points = [], reverse = false }) {
  return (
    <div
      className={`flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-16 ${
        reverse ? "lg:flex-row-reverse" : ""
      }`}
    >
      <div className="lg:sticky lg:top-28 lg:w-5/12">
        <h2 className="text-2xl font-bold tracking-tight text-ink md:text-3xl">
          {title}
        </h2>
        {lead ? (
          <p className="mt-4 text-base leading-relaxed text-ink-muted">{lead}</p>
        ) : null}
      </div>
      <div className="lg:w-7/12">
        <ul className="space-y-5">
          {points.map((point) => (
            <li
              key={point.title}
              className="border-b border-line pb-5 last:border-0 last:pb-0"
            >
              <h3 className="text-lg font-semibold text-ink">{point.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-muted">
                {point.description}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function ProcessSteps({ steps }) {
  return <ProcessRail steps={steps} />;
}

export function OutcomesList({ title, lead, outcomes }) {
  return (
    <ServiceSection muted>
      <ServiceSectionHeader title={title} lead={lead} />
      <div className="mt-10 grid max-w-4xl gap-3 sm:grid-cols-2">
        {outcomes.map((outcome) => (
          <div
            key={outcome}
            className="flex items-start gap-3 rounded-xl border border-line bg-surface px-5 py-4"
          >
            <span
              className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#1c8c80]"
              aria-hidden
            />
            <p className="text-sm leading-relaxed text-ink-muted">{outcome}</p>
          </div>
        ))}
      </div>
    </ServiceSection>
  );
}

export function ServiceFaq({ items }) {
  return (
    <ServiceSection>
      <ServiceSectionHeader
        title="Common questions"
        lead="Straight answers about scope, delivery, and what working with us looks like."
      />
      <FaqList items={items} />
    </ServiceSection>
  );
}

export function ServiceBottomCta({
  title = "Ready to discuss your use case?",
  description = "Share your operations context. We will map a practical path from idea to a production system.",
  primaryText = "Book a discovery call",
  secondaryText = "Write to us",
}) {
  return (
    <CtaBand
      title={title}
      description={description}
      primaryText={primaryText}
      primaryHref={CALENDLY_URL}
      secondaryText={secondaryText}
      secondaryHref="/contact"
    />
  );
}

export function ServiceAudience({ forItems, notForItems }) {
  return (
    <ServiceSection muted>
      <ServiceSectionHeader
        title="Who this engagement is for"
        lead="Clear boundaries save time. Here is when this service is a strong match."
      />
      <AudienceSplit forItems={forItems} notForItems={notForItems} />
    </ServiceSection>
  );
}

export function ScenarioGrid({ items }) {
  return (
    <div className="mt-12 grid gap-4 md:grid-cols-2">
      {items.map((item) => (
        <div
          key={item.title}
          className="rounded-2xl border border-line bg-surface p-6"
        >
          <h3 className="text-lg font-semibold text-ink">{item.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink-muted">
            {item.description}
          </p>
          {item.outcome ? (
            <p className="mt-4 text-sm font-medium text-[#1c8c80]">
              {item.outcome}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function RelatedServices({ items }) {
  return (
    <ServiceSection muted>
      <ServiceSectionHeader
        title="Other ways we can help"
        lead="Many programs combine more than one service."
      />
      <div className="mt-10 flex flex-wrap gap-3">
        {items.map((item) => (
          <Link key={item.href} href={item.href} className="landing-btn-secondary">
            {item.label}
          </Link>
        ))}
        <Link href="/how-we-work" className="landing-btn-secondary">
          How we work
        </Link>
        <Link href="/use-cases" className="landing-btn-secondary">
          Use cases
        </Link>
      </div>
    </ServiceSection>
  );
}
